import React, { useState } from 'react'
import { COLORS, Chip, DAY_NAMES, EmptyState, INPUT_STYLE, SELECT_SMALL, BTN_SMALL, pct } from '../FieldOpsKit'
import { FREQUENCIES, REPS } from '../demoData'
import { routesOf } from '../store'

/**
 * Agenda setup (editors): which weekday and how often each store is visited, and the order inside the day.
 * Changing a store regenerates its future planned visits from tomorrow on; past and done visits never change.
 */
const DAYS = [1, 2, 3, 4, 5]

export default function AgendaTab({ ctx }) {
    const sales = ctx.state.sales
    const [rep, setRep] = useState('RT')
    const [route, setRoute] = useState('all')
    const editable = ctx.can.editGoals
    const stores = sales.stores.filter((s) => (rep === 'all' || s.rep === rep) && (route === 'all' || s.route === route))
    const max = Math.max(1, ...DAYS.map((d) => stores.filter((s) => s.day === d).length))
    const freqLabel = (k) => FREQUENCIES.filter((f) => f.key === k)[0].label

    const update = (store, patch) => {
        if (!editable) return
        const day = patch.day !== undefined ? patch.day : store.day
        const freq = patch.freq !== undefined ? patch.freq : store.freq
        ctx.dispatch({ type: 'AGENDA_SET', storeId: store.id, day: day, freq: freq, by: ctx.me.name })
        ctx.toast(store.name + ': ' + DAY_NAMES[day] + ' · ' + freqLabel(freq) + '. Future planned visits were updated.')
    }

    return (
        <React.Fragment>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <select value={rep} onChange={(e) => setRep(e.target.value)} style={INPUT_STYLE} aria-label="Salesperson">
                    {REPS.map((r) => <option key={r} value={r}>Salesperson {r}</option>)}
                    <option value="all">All salespeople</option>
                </select>
                <select value={route} onChange={(e) => setRoute(e.target.value)} style={INPUT_STYLE} aria-label="Route">
                    <option value="all">All routes</option>
                    {routesOf(sales).map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <span style={{ fontSize: '0.78rem', color: COLORS.slate }}>{stores.length} stores · changes apply from tomorrow, past visits are never rewritten.</span>
            </div>
            {!editable ? <div style={{ fontSize: '0.78rem', color: COLORS.slate, marginBottom: 10 }}>Your demo user can see the agenda but not change it (only editors and the super admin).</div> : null}
            {rep === 'all' ? <div style={{ fontSize: '0.78rem', color: COLORS.slate, marginBottom: 10 }}>Pick one salesperson to reorder stops: the order of the day belongs to each salesperson.</div> : null}

            {stores.length === 0 ? <EmptyState title="No stores" text="Pick another salesperson or route." /> : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12, alignItems: 'start' }}>
                    {DAYS.map((d) => {
                        const list = stores.filter((s) => s.day === d).sort((a, b) => a.seq - b.seq)
                        return (
                            <div key={d} style={{ background: COLORS.soft, borderRadius: 12, padding: 10 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                    <strong style={{ color: COLORS.navy }}>{DAY_NAMES[d]}</strong>
                                    <Chip tone={list.length === 0 ? 'gray' : list.length > max * 0.8 && max > 5 ? 'amber' : 'blue'}>{list.length} stops</Chip>
                                </div>
                                <div style={{ background: '#FFFFFF', borderRadius: 999, height: 5, overflow: 'hidden', marginBottom: 10 }}>
                                    <div style={{ width: pct(list.length, max) + '%', height: '100%', background: COLORS.blue }}></div>
                                </div>
                                {list.length === 0 ? <div style={{ fontSize: '0.78rem', color: COLORS.muted, padding: '10px 4px' }}>No stores scheduled.</div> : list.map((s, i) => (
                                    <div key={s.id} style={{ background: '#FFFFFF', border: '1px solid ' + COLORS.line, borderRadius: 10, padding: '8px 10px', marginBottom: 8 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontWeight: 700, fontSize: '0.83rem', color: COLORS.navy }}>{i + 1}. {s.name}</div>
                                                <div style={{ fontSize: '0.7rem', color: COLORS.slate }}>{s.route} · {s.city}{rep === 'all' ? ' · ' + s.rep : ''}</div>
                                            </div>
                                            {rep !== 'all' ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <button type="button" disabled={!editable || i === 0} onClick={() => ctx.dispatch({ type: 'AGENDA_MOVE', storeId: s.id, dir: -1 })} style={{ ...BTN_SMALL, padding: '0 6px', opacity: !editable || i === 0 ? 0.3 : 1 }} aria-label="Move up">▲</button>
                                                    <button type="button" disabled={!editable || i === list.length - 1} onClick={() => ctx.dispatch({ type: 'AGENDA_MOVE', storeId: s.id, dir: 1 })} style={{ ...BTN_SMALL, padding: '0 6px', opacity: !editable || i === list.length - 1 ? 0.3 : 1 }} aria-label="Move down">▼</button>
                                                </div>
                                            ) : null}
                                        </div>
                                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                            <select disabled={!editable} value={s.day} onChange={(e) => update(s, { day: Number(e.target.value) })} style={{ ...SELECT_SMALL, flex: 1 }} aria-label={'Day for ' + s.name}>
                                                {DAYS.map((x) => <option key={x} value={x}>{DAY_NAMES[x]}</option>)}
                                            </select>
                                            <select disabled={!editable} value={s.freq} onChange={(e) => update(s, { freq: e.target.value })} style={{ ...SELECT_SMALL, flex: 1 }} aria-label={'Frequency for ' + s.name}>
                                                {FREQUENCIES.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    })}
                </div>
            )}
        </React.Fragment>
    )
}
